import { DELIVERY_METHOD_KIND } from "@dransay/api/models";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import {
	hasSeenAvailabilityModal,
	markAvailabilityModalSeen,
} from "./express-courier-availability-cache";
import { useExpressCourierPharmacies } from "./use-express-courier-pharmacies";

const PRODUCT_LIST_PATHNAME = "/products";

interface UsePlpExpressAvailabilityModalParams {
	/** Rollout flag AND an authenticated customer. */
	enabled: boolean;
	postalCode: string | undefined;
	expressVendorIds: readonly string[];
	/**
	 * Switches the PLP onto the express pharmacy. Must go through vendor
	 * selection (not a bare navigate) so URL + selection state stay in sync.
	 */
	onNavigateToPharmacy: (vendorId: string) => void | Promise<void>;
}

/**
 * Owns when the PLP express availability modal appears.
 *
 * The modal is a discovery nudge, so it is offered at most once per postal code
 * per session. The seen-marker lives in the express availability cache
 * namespace, which means an address change invalidates it along with that
 * area's cached verdict and the offer is re-armed for the new area.
 */
export function usePlpExpressAvailabilityModal({
	enabled,
	postalCode,
	expressVendorIds,
	onNavigateToPharmacy,
}: UsePlpExpressAvailabilityModalParams) {
	const { pathname } = useLocation();
	const [open, setOpen] = useState(false);

	const canOffer =
		enabled &&
		!!postalCode &&
		expressVendorIds.length > 0 &&
		pathname === PRODUCT_LIST_PATHNAME;

	const { pharmacies } = useExpressCourierPharmacies({
		postalCode,
		vendorIds: expressVendorIds,
		enabled: canOffer,
	});

	const targetVendorId = pharmacies[0]?.vendorId;

	useEffect(() => {
		if (!canOffer || !postalCode || !targetVendorId) {
			return;
		}
		if (hasSeenAvailabilityModal(postalCode)) {
			return;
		}
		markAvailabilityModalSeen(postalCode);
		setOpen(true);
	}, [canOffer, postalCode, targetVendorId]);

	const showCourierPharmacies = useCallback(() => {
		if (!targetVendorId) {
			return;
		}
		void onNavigateToPharmacy(targetVendorId);
	}, [onNavigateToPharmacy, targetVendorId]);

	return {
		isModalOpen: open && !!targetVendorId,
		setModalOpen: setOpen,
		showCourierPharmacies,
	};
}
